<script setup lang="ts">
  import {
    DefaultTable,
    TableHeader,
    TableTitle,
    ConfirmationModal,
    FilterSortAndDelete,
    SearchNoResults,
  } from '@/shared/ui';
  import BranchTableRow from './BranchTableRow.vue';
  import type { Branch } from '@/entities/Branch';
  import { useModal } from 'vue-final-modal';
  import Trash from '@/shared/assets/icons/TrashIcon.svg';
  import { OrderOptions } from '@/shared/lib';

  const props = withDefaults(
    defineProps<{
      orderOptions?: OrderOptions[];
      branches: Branch[];
      loading?: boolean;
      showNoResults?: boolean;
    }>(),
    {
      orderOptions: () => [],
      branches: () => [],
      loading: false,
      showNoResults: false,
    }
  );

  const emit = defineEmits<{
    'update:sort': [sort: string, order: string];
    delete: [id: number];
  }>();

  const handleSortOptions = (sortCamp: string, orderFlow: string) => {
    emit('update:sort', sortCamp, orderFlow);
  };

  const confirmDeleteModal = (id: number) => {
    if (!props.branches) {
      throw new Error('Não há filiais para deletar');
    }
    if (!id) {
      throw new Error('ID não definido');
    }

    const branchToDelete = props.branches.find((branch) => branch.id === id);
    if (branchToDelete?.isMain || branchToDelete?.name === 'Matriz') {
      return;
    }

    const { open: openConfirmation, close: closeConfirmation } = useModal({
      component: ConfirmationModal,
      attrs: {
        icon: Trash,
        iconAlt: 'Icone de Deleção',
        title: 'Tem certeza que deseja deletar a filial?',
        text: 'A ação não pode ser desfeita.',
        actionButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        onClose() {
          closeConfirmation();
        },
        async onConfirm() {
          emit('delete', id);
          closeConfirmation();
        },
      },
    });

    openConfirmation();
  };

  defineExpose({
    confirmDeleteModal,
  });
</script>

<template>
  <DefaultTable>
    <thead class="bg-white-01 divide-y divide-gray-02">
      <tr>
        <td
          colspan="4"
          class="px-6 py-4"
        >
          <div class="flex w-full items-center justify-between">
            <TableTitle text="Lista de Filiais" />
            <FilterSortAndDelete
              :has-selected="false"
              :selected-count="0"
              :selected-documents="[]"
              :order-options="orderOptions"
              :has-filter="false"
              :has-delete="false"
              @update:sort="handleSortOptions"
            />
          </div>
        </td>
      </tr>
      <tr v-if="branches.length > 0">
        <TableHeader
          text="Filiais"
          class="text-xs/5"
        />
        <TableHeader
          text="Colaboradores"
          class="text-xs/5"
        />
        <TableHeader
          text="Data de Criação"
          class="text-xs/5"
        />
        <TableHeader
          text="Ações"
          class="text-xs/5"
        />
      </tr>
    </thead>
    <tbody class="bg-white-01 divide-y divide-gray-02">
      <BranchTableRow
        v-for="branch in branches"
        :key="branch.id"
        :branch="branch"
        @delete="confirmDeleteModal"
      />
      <tr v-if="showNoResults">
        <td colspan="100">
          <SearchNoResults />
        </td>
      </tr>
    </tbody>
  </DefaultTable>
</template>
